import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { getEmailSettings, updateEmailSettings, sendTestEmail } from '../../firebase/services/emailService';
import { EmailSettings as EmailSettingsType } from '../../firebase/services/emailService';

const EMAIL_PROVIDERS = [
  { label: 'Gmail', value: 'gmail', smtpHost: 'smtp.gmail.com', smtpPort: 465, security: 'SSL/TLS', help: 'Gmail requires App Passwords and 2-Step Verification.' },
  { label: 'Google Workspace', value: 'google_workspace', smtpHost: 'smtp.gmail.com', smtpPort: 465, security: 'SSL/TLS', help: 'Google Workspace requires App Passwords and 2-Step Verification.' },
  { label: 'Outlook', value: 'outlook', smtpHost: 'smtp.office365.com', smtpPort: 587, security: 'STARTTLS', help: 'Outlook/Office 365 uses STARTTLS on port 587.' },
  { label: 'Yahoo', value: 'yahoo', smtpHost: 'smtp.mail.yahoo.com', smtpPort: 465, security: 'SSL/TLS', help: 'Yahoo requires App Passwords.' },
  { label: 'iCloud', value: 'icloud', smtpHost: 'smtp.mail.me.com', smtpPort: 587, security: 'STARTTLS', help: 'iCloud uses STARTTLS on port 587.' },
  { label: 'Zoho', value: 'zoho', smtpHost: 'smtp.zoho.com', smtpPort: 465, security: 'SSL/TLS', help: 'Zoho uses SSL/TLS on port 465.' },
  { label: 'AOL', value: 'aol', smtpHost: 'smtp.aol.com', smtpPort: 465, security: 'SSL/TLS', help: 'AOL uses SSL/TLS on port 465.' },
  { label: 'Yandex', value: 'yandex', smtpHost: 'smtp.yandex.com', smtpPort: 465, security: 'SSL/TLS', help: 'Yandex uses SSL/TLS on port 465.' },
  { label: 'GMX', value: 'gmx', smtpHost: 'mail.gmx.com', smtpPort: 465, security: 'SSL/TLS', help: 'GMX uses SSL/TLS on port 465.' },
  { label: 'Mail.com', value: 'mailcom', smtpHost: 'smtp.mail.com', smtpPort: 465, security: 'SSL/TLS', help: 'Mail.com uses SSL/TLS on port 465.' },
  { label: 'ProtonMail (Bridge)', value: 'protonmail', smtpHost: '127.0.0.1', smtpPort: 1025, security: 'None', help: 'ProtonMail requires the Bridge app.' },
  { label: 'Custom', value: 'custom', smtpHost: '', smtpPort: 587, security: '', help: 'Enter your own SMTP settings.' },
];

const EmailSettings: React.FC = () => {
  const [settings, setSettings] = useState<EmailSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testSuccess, setTestSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const emailSettings = await getEmailSettings();
      setSettings(emailSettings || {
        smtpHost: '',
        smtpPort: 587,
        smtpUsername: '',
        smtpPassword: '',
        fromEmail: '',
        fromName: '',
        enabled: false
      });
    } catch (error) {
      setError('Failed to load email settings');
      console.error('Error loading email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof EmailSettingsType) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (settings) {
      setSettings({
        ...settings,
        [field]: field === 'smtpPort' 
          ? parseInt(event.target.value) || 587 
          : field === 'enabled'
            ? event.target.checked
            : event.target.value
      });
    }
  };

  const handleSubmit = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await updateEmailSettings(settings);
      setSuccess('Email settings updated successfully');
    } catch (error) {
      setError('Failed to update email settings');
      console.error('Error updating email settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setTestSending(true);
    setTestError(null);
    setTestSuccess(null);
    try {
      if (!settings) throw new Error('No SMTP settings available');
      const result = await sendTestEmail(
        testEmail,
        'Test Email from TendPOS',
        '<p>This is a test email sent from your email settings page.</p>',
        {},
        settings
      );
      if (result.success) {
        setTestSuccess('Test email sent successfully!');
      } else {
        setTestError(result.error || 'Failed to send test email. Check your SMTP settings.');
      }
    } catch (err: any) {
      setTestError(err?.message || 'Failed to send test email. Check your SMTP settings.');
    } finally {
      setTestSending(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Email Settings
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" noValidate autoComplete="off">
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(settings?.enabled)}
                onChange={(e) => handleChange('enabled')(e as any)}
              />
            }
            label="Enable Email Service"
          />

          <TextField
            select
            label="Email Provider"
            fullWidth
            value={settings?.provider || ''}
            onChange={e => {
              const provider = EMAIL_PROVIDERS.find(p => p.value === e.target.value);
              setSettings(s => ({
                ...s!,
                provider: provider?.value || '',
                smtpHost: provider?.smtpHost || '',
                smtpPort: provider?.smtpPort || 587,
                security: provider?.security || '',
              }));
            }}
            helperText="Select your email provider to auto-fill SMTP settings."
            margin="normal"
          >
            {EMAIL_PROVIDERS.map(provider => (
              <MenuItem key={provider.value} value={provider.value}>{provider.label}</MenuItem>
            ))}
          </TextField>

          {settings?.provider && EMAIL_PROVIDERS.find(p => p.value === settings.provider)?.help && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {EMAIL_PROVIDERS.find(p => p.value === settings.provider)?.help}
            </Alert>
          )}

          <TextField
            fullWidth
            label="SMTP Host"
            value={settings?.smtpHost || ''}
            onChange={handleChange('smtpHost')}
            margin="normal"
          />

          <TextField
            fullWidth
            label="SMTP Port"
            type="number"
            value={settings?.smtpPort || 587}
            onChange={handleChange('smtpPort')}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Security (SSL/TLS/STARTTLS/None)"
            value={settings?.security || ''}
            onChange={handleChange('security')}
            margin="normal"
          />

          <TextField
            fullWidth
            label="SMTP Username"
            value={settings?.smtpUsername || ''}
            onChange={handleChange('smtpUsername')}
            margin="normal"
          />

          <TextField
            fullWidth
            label="SMTP Password"
            type="password"
            value={settings?.smtpPassword || ''}
            onChange={handleChange('smtpPassword')}
            margin="normal"
          />

          <TextField
            fullWidth
            label="From Email"
            value={settings?.fromEmail || ''}
            onChange={handleChange('fromEmail')}
            margin="normal"
          />

          <TextField
            fullWidth
            label="From Name"
            value={settings?.fromName || ''}
            onChange={handleChange('fromName')}
            margin="normal"
          />

          <Box mt={2} display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setTestDialogOpen(true)}
            >
              Send Test Email
            </Button>
          </Box>
        </Box>
        {/* Test Email Dialog */}
        <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)}>
          <DialogTitle>Send Test Email</DialogTitle>
          <DialogContent>
            <TextField
              label="Recipient Email"
              fullWidth
              type="email"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              margin="normal"
              disabled={testSending}
            />
            {testError && <Alert severity="error" sx={{ mt: 1 }}>{testError}</Alert>}
            {testSuccess && <Alert severity="success" sx={{ mt: 1 }}>{testSuccess}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTestDialogOpen(false)} disabled={testSending}>Cancel</Button>
            <Button onClick={handleSendTest} variant="contained" disabled={testSending || !testEmail}>
              {testSending ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default EmailSettings; 